import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { PageHeader } from "@/components/layout/PageHeader";
import { Plus, Trash, X } from "@phosphor-icons/react";

export default function SiteMasterPage() {
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    site_number: "", name: "", address: "", country: "",
    property_location: "", asset_register_location: "", hr_location: "", inventory_location: "",
  });

  const load = async () => { const { data } = await api.get("/alm/sites"); setRows(data); };
  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    await api.post("/alm/sites", form);
    setOpen(false);
    setForm({ site_number: "", name: "", address: "", country: "", property_location: "", asset_register_location: "", hr_location: "", inventory_location: "" });
    load();
  };

  const remove = async (id) => { if (window.confirm("Delete this site?")) { await api.delete(`/alm/sites/${id}`); load(); } };

  const importCsv = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    const { data } = await api.post("/alm/sites/import-csv", fd, { headers: { "Content-Type": "multipart/form-data" } });
    alert(`Imported ${data.imported}, skipped ${data.skipped}${data.errors?.length ? `\nErrors:\n${data.errors.join("\n")}` : ""}`);
    e.target.value = "";
    load();
  };

  return (
    <div>
      <PageHeader
        eyebrow="Asset Lifecycle"
        title="Site Master"
        subtitle="Sites consolidate property, asset register, HR and inventory locations under a unique site number."
        testid="alm-sites-title"
        actions={
          <>
            <label data-testid="alm-site-import-btn" className="border border-gray-300 px-4 py-2 text-xs font-bold uppercase tracking-wider hover:bg-gray-50 inline-flex items-center gap-2 cursor-pointer">
              Import CSV
              <input type="file" accept=".csv,text/csv" onChange={importCsv} className="hidden" data-testid="alm-site-import-input" />
            </label>
            <button onClick={() => setOpen(true)} data-testid="alm-site-new-btn" className="bg-[#0020C2] text-white px-4 py-2 text-xs font-bold uppercase tracking-wider hover:bg-[#001AA3] flex items-center gap-2">
              <Plus size={14} weight="bold" /> New Site
            </button>
          </>
        }
      />
      <div className="p-8">
        <div className="border border-gray-200 bg-white overflow-x-auto">
          <table className="w-full text-sm" data-testid="alm-sites-table">
            <thead className="bg-gray-50">
              <tr className="text-left">
                {["Site #", "Name", "Country", "Property Loc.", "Asset Reg.", "HR Loc.", "Inventory Loc.", ""].map(h => (
                  <th key={h} className="px-4 py-3 text-[10px] tracking-[0.2em] uppercase font-bold text-gray-500 border-b border-gray-300">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map(r => (
                <tr key={r.id} className="border-b border-gray-100 hover:bg-gray-50">
                  <td className="px-4 py-3 font-mono text-xs font-bold">{r.site_number}</td>
                  <td className="px-4 py-3 font-semibold">{r.name}</td>
                  <td className="px-4 py-3 text-xs">{r.country}</td>
                  <td className="px-4 py-3 text-xs">{r.property_location}</td>
                  <td className="px-4 py-3 text-xs">{r.asset_register_location}</td>
                  <td className="px-4 py-3 text-xs">{r.hr_location}</td>
                  <td className="px-4 py-3 text-xs">{r.inventory_location}</td>
                  <td className="px-4 py-3 text-right">
                    <button onClick={() => remove(r.id)} data-testid={`alm-site-del-${r.id}`} className="text-[#DC2626] hover:underline text-xs font-bold inline-flex items-center gap-1">
                      <Trash size={12} weight="bold" />
                    </button>
                  </td>
                </tr>
              ))}
              {rows.length === 0 && <tr><td colSpan={8} className="px-4 py-12 text-center text-sm text-gray-500">No sites yet.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      {open && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-stretch justify-end" onClick={() => setOpen(false)}>
          <form onClick={e => e.stopPropagation()} onSubmit={create} className="w-full max-w-md bg-white h-full overflow-y-auto" data-testid="alm-site-form">
            <div className="p-6 border-b border-gray-200 flex items-center justify-between">
              <div>
                <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-gray-500">Create</div>
                <h2 className="text-2xl font-black tracking-tight">New Site</h2>
              </div>
              <button type="button" onClick={() => setOpen(false)}><X size={18} /></button>
            </div>
            <div className="p-6 space-y-4">
              {[
                ["site_number", "Site Number *", true],
                ["name", "Site Name *", true],
                ["address", "Address", false],
                ["country", "Country", false],
                ["property_location", "Property Location", false],
                ["asset_register_location", "Asset Register Location", false],
                ["hr_location", "HR Location", false],
                ["inventory_location", "Inventory Location", false],
              ].map(([k, label, required]) => (
                <div key={k}>
                  <label className="text-xs font-bold uppercase tracking-wider text-gray-600 block mb-1.5">{label}</label>
                  <input type="text" required={required} value={form[k]} onChange={e => setForm({ ...form, [k]: e.target.value })} data-testid={`alm-site-${k}`} className="w-full border border-gray-300 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#0020C2]" />
                </div>
              ))}
            </div>
            <div className="p-6 border-t border-gray-200">
              <button type="submit" data-testid="alm-site-submit" className="w-full bg-[#0020C2] text-white py-3 text-sm font-bold uppercase tracking-wider hover:bg-[#001AA3]">Create Site</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
